import { useMutation, useQuery } from '@apollo/client';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { summarizePredicate } from './predicate-builder';
import { PriceRuleFormDialog } from './PriceRuleFormDialog';
import { DEACTIVATE_PRICE_RULE_MUTATION, PRICE_RULE_SUBJECTS_LOOKUP, PRICE_RULES_QUERY } from './pricing.gql';

import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface PriceRule {
  id: string;
  subject_kind: string;
  subject_id: string;
  subject_name: string | null;
  subject_code: string | null;
  branch_id: string | null;
  priority: number;
  predicate_json: string;
  price_cents: string;
  note: string | null;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

interface BranchLookupResponse {
  branches: { id: string; name: string }[];
}

interface PriceRulesResponse {
  priceRules: {
    items: PriceRule[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function PriceRulesPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_manage = user?.permissions.includes('pricing.rules.manage') ?? false;

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PriceRule | null>(null);

  const { data, loading, refetch } = useQuery<PriceRulesResponse>(PRICE_RULES_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
    },
    fetchPolicy: 'cache-and-network',
  });
  // `branches` here is the full, unpaginated list already fetched for the form dialog's
  // branch select — reused as-is to join `branch_id -> name` in the table.
  const { data: branch_lookup } = useQuery<BranchLookupResponse>(PRICE_RULE_SUBJECTS_LOOKUP, {
    fetchPolicy: 'cache-first',
  });
  const branch_name_by_id = new Map((branch_lookup?.branches ?? []).map((b) => [b.id, b.name]));

  const [deactivate] = useMutation(DEACTIVATE_PRICE_RULE_MUTATION);

  const onClickDeactivate = async (rule: PriceRule): Promise<void> => {
    try {
      await deactivate({ variables: { id: rule.id } });
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('pricing.deactivated_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: err instanceof Error ? err.message : 'deactivate_failed',
      });
    }
  };

  const columns: CrudColumn<PriceRule>[] = [
    {
      key: 'subject',
      header_key: 'pricing.subject',
      render: (r) => (
        <div className="min-w-0">
          <Badge variant="outline">{t(`pricing.kind_${r.subject_kind}`)}</Badge>
          <p className="truncate text-sm">
            {r.subject_name ? (
              <>
                <span className="font-mono text-xs text-muted-foreground">{r.subject_code}</span>{' '}
                {r.subject_name}
              </>
            ) : (
              <span className="font-mono text-xs text-muted-foreground">{r.subject_id.slice(0, 8)}</span>
            )}
          </p>
        </div>
      ),
    },
    {
      key: 'branch',
      header_key: 'pricing.branch',
      width: 'w-32',
      render: (r) => (r.branch_id ? branch_name_by_id.get(r.branch_id) ?? r.branch_id.slice(0, 8) : t('pricing.branch_global')),
    },
    {
      key: 'priority',
      header_key: 'pricing.priority',
      align: 'right',
      width: 'w-24',
      render: (r) => <span className="tabular-nums">{r.priority}</span>,
    },
    {
      key: 'price',
      header_key: 'pricing.price',
      align: 'right',
      width: 'w-32',
      render: (r) => <span className="tabular-nums">{formatCentsString(r.price_cents)}</span>,
    },
    {
      key: 'summary',
      header_key: 'pricing.summary_label',
      render: (r) => (
        <span className="line-clamp-2 max-w-md text-xs text-muted-foreground">
          {summarizePredicate(r.predicate_json, t)}
        </span>
      ),
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-28',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`pricing.status_${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-48',
      render: (r) =>
        can_manage ? (
          <div className="flex justify-end gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(r)}>
              <Pencil className="h-4 w-4" aria-hidden />
              <span>{t('common.edit')}</span>
            </Button>
            {r.status === 'active' ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  void onClickDeactivate(r);
                }}
              >
                {t('pricing.deactivate')}
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight">{t('pricing.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('pricing.subtitle')}</p>
      </div>
      <CrudTable<PriceRule>
        rows={data?.priceRules.items ?? []}
        columns={columns}
        loading={loading}
        page={data?.priceRules.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="pricing.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            primary_action={
              can_manage ? (
                <Button type="button" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('pricing.new_rule')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />
      {creating ? (
        <PriceRuleFormDialog
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            showToast({
              tone: 'success',
              title: t('alerts.create_success_title'),
              body: t('pricing.created_body'),
            });
            await refetch();
          }}
        />
      ) : null}
      {editing ? (
        <PriceRuleFormDialog
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            showToast({
              tone: 'success',
              title: t('alerts.update_success_title'),
              body: t('pricing.updated_body'),
            });
            await refetch();
          }}
        />
      ) : null}
    </div>
  );
}
